import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

@Directive({
  selector: '[miqAgStickyHeader]',
  standalone: false,
})
export class AgStickyHeaderDirective implements AfterViewInit, OnDestroy {
  @Input() stickyTop = 0;
  private AGheader: HTMLElement;
  // ag-grid v32: div.ag-root[data-ref="gridBody"] is the full-width shell — use for width & scroll detection
  private AGBody: HTMLElement;
  // div[data-ref="eBody"] is the inner rows container — needs paddingTop to push rows below sticky header
  private AGBodyInner: HTMLElement;
  private AGHorizontalScroll: HTMLElement;
  private sticky = false;
  private originalHeaderStyles: Record<string, string> = {};
  private originalScrollStyles: Record<string, string> = {};
  private originalBodyStyles: Record<string, string> = {};
  #subs = new Subscription();

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // ag-grid v32 actual rendered DOM (verified from live HTML):
    //   div.ag-root-wrapper
    //     div.ag-root-wrapper-body[data-ref="rootWrapperBody"]
    //       div.ag-root[data-ref="gridBody"]         ← outer shell (width source, scroll detection)
    //         div.ag-header                          ← sticky header target
    //         div.ag-body[data-ref="eBody"]          ← inner body (needs paddingTop)
    //         div.ag-body-horizontal-scroll          ← horizontal scrollbar
    this.AGBody = this.el.nativeElement.querySelector('[data-ref="gridBody"]') as HTMLElement;
    this.AGheader = this.el.nativeElement.querySelector('.ag-header') as HTMLElement;
    this.AGBodyInner = this.el.nativeElement.querySelector('[data-ref="eBody"]') as HTMLElement;
    this.AGHorizontalScroll = this.el.nativeElement.querySelector('.ag-body-horizontal-scroll') as HTMLElement;

    if (this.AGheader) {
      this.saveOriginalStyles(this.AGheader, this.originalHeaderStyles);
    }
    if (this.AGHorizontalScroll) {
      this.saveOriginalStyles(this.AGHorizontalScroll, this.originalScrollStyles);
    }
    if (this.AGBodyInner) {
      this.saveOriginalBodyStyles(this.AGBodyInner, this.originalBodyStyles);
    }

    // the handlers only restyle through Renderer2, so scrolling must not run app-wide change detection
    this.zone.runOutsideAngular(() => {
      this.#subs.add(fromEvent(document, 'scroll', { passive: true }).subscribe(() => this.onWindowScroll()));
      this.#subs.add(fromEvent(window, 'resize', { passive: true }).subscribe(() => this.onWindowResize()));
    });
  }

  private saveOriginalStyles(element: HTMLElement, styles: Record<string, string>) {
    styles.position = element.style.position;
    styles.top = element.style.top;
    styles.zIndex = element.style.zIndex;
    styles.width = element.style.width;
  }

  private saveOriginalBodyStyles(element: HTMLElement, styles: Record<string, string>) {
    styles.paddingTop = element.style.paddingTop;
  }

  private setStyles(element: HTMLElement, styles: Record<string, string>) {
    Object.entries(styles).forEach(([key, value]) => {
      this.renderer.setStyle(element, key, value);
    });
  }

  private restoreOriginalStyles(element: HTMLElement, styles: Record<string, string>) {
    Object.entries(styles).forEach(([key, value]) => {
      this.renderer.setStyle(element, key, value);
    });
  }

  onWindowScroll(): void {
    if (!this.AGheader || !this.AGBody) return;

    const shouldStick = !this.sticky && this.AGheader.getBoundingClientRect().top <= this.stickyTop;
    const shouldUnstick =
      this.sticky &&
      this.AGBody.getBoundingClientRect().top - this.AGheader.getBoundingClientRect().height > this.stickyTop - 50;

    if (shouldStick) {
      this.sticky = true;
      this.setStickyStyles();
      return;
    }

    if (shouldUnstick) {
      this.sticky = false;
      this.restoreOriginalStyles(this.AGHorizontalScroll, this.originalScrollStyles);
      this.restoreOriginalStyles(this.AGheader, this.originalHeaderStyles);
      this.restoreOriginalStyles(this.AGBodyInner, this.originalBodyStyles);
    }
  }

  onWindowResize(): void {
    if (this.sticky) {
      this.setStickyStyles();
    }
  }

  private setStickyStyles() {
    // ag-grid v32: div.ag-root[data-ref="gridBody"] is 100% wide and is the correct width reference
    const gridWidth = `${this.AGBody.offsetWidth}px`;

    this.setStyles(this.AGHorizontalScroll, {
      position: 'fixed',
      top: `${this.stickyTop}px`,
      zIndex: '995',
      width: gridWidth,
    });

    this.setStyles(this.AGheader, {
      position: 'fixed',
      top: `${this.stickyTop + this.AGHorizontalScroll.offsetHeight}px`,
      zIndex: '995',
      width: gridWidth,
    });

    // Push the row content down so it doesn't slide under the fixed header
    this.setStyles(this.AGBodyInner, {
      paddingTop: `${this.AGheader.offsetHeight + this.AGHorizontalScroll.offsetHeight}px`,
    });
  }

  ngOnDestroy(): void {
    this.#subs.unsubscribe();
    if (this.AGheader) {
      this.restoreOriginalStyles(this.AGheader, this.originalHeaderStyles);
    }
    if (this.AGHorizontalScroll) {
      this.restoreOriginalStyles(this.AGHorizontalScroll, this.originalScrollStyles);
    }
    if (this.AGBodyInner) {
      this.restoreOriginalStyles(this.AGBodyInner, this.originalBodyStyles);
    }
    this.sticky = false;
    this.originalHeaderStyles = {};
    this.originalScrollStyles = {};
    this.originalBodyStyles = {};
  }
}
